import { useTranslation } from "react-i18next";
import { Card } from "@opencast/appkit";
import { graphql, useFragment } from "react-relay";

import { isSynced, keyOfId } from "../../util";
import type { Fields } from "../../relay";
import {
    SeriesBlockData$data, SeriesBlockData$key,
} from "./__generated__/SeriesBlockData.graphql";
import {
    SeriesBlockSeriesData$data,
    SeriesBlockSeriesData$key,
} from "./__generated__/SeriesBlockSeriesData.graphql";
import { VideoListBlock, VideoListBlockContainer, Order, VideoListLayout } from "./VideoList";
import { ManageSeriesDetailsRoute } from "../../routes/manage/Series/SeriesDetails";
import { DirectSeriesRoute, SeriesRoute } from "../../routes/Series";
import { MovingTruck } from "../Waiting";


// ==============================================================================================
// ===== Data plumbing components (no UI stuff)
// ==============================================================================================

type SharedProps = {
    realmPath: string | null;
    editMode?: boolean;
    layoutParam?: VideoListLayout;
    orderParam?: Order;
};

const blockFragment = graphql`
    fragment SeriesBlockData on SeriesBlock {
        series {
            ...SeriesBlockSeriesData
        }
        showTitle
        showMetadata
        order
        layout
    }
`;

const seriesFragment = graphql`
    fragment SeriesBlockSeriesData on Series {
        id
        title
        created
        creators
        description
        state
        isBookmark
        canWrite
        hasPublicVideos
        entries {
            __typename
            ...on AuthorizedEvent { ...VideoListEventData @arguments(includeSeries: false) }
            ...on NotAllowed { __typename }
        }
    }
`;

type FromBlockProps = SharedProps & {
    fragRef: SeriesBlockData$key;
};

export const SeriesBlockFromBlock: React.FC<FromBlockProps> = ({ fragRef, ...rest }) => {
    const { t } = useTranslation();
    const { series, ...block } = useFragment(blockFragment, fragRef);

    return series == null && rest.editMode
        ? <Card kind="error">{t("series.deleted-block")}</Card>
        : series != null && <SeriesBlockFromSeries fragRef={series} {...rest} {...block} />;
};

type BlockProps = Partial<Omit<Fields<SeriesBlockData$data>, "series">>;

type SharedFromSeriesProps = SharedProps & BlockProps & {
    title?: string;
    activeEventId?: string;
};

type FromSeriesProps = SharedFromSeriesProps & {
    fragRef: SeriesBlockSeriesData$key;
};

export const SeriesBlockFromSeries: React.FC<FromSeriesProps> = (
    { fragRef, ...rest },
) => {
    const series = useFragment(seriesFragment, fragRef);
    return <SeriesBlock series={series} {...rest} />;
};

type Props = SharedFromSeriesProps & {
    series: SeriesBlockSeriesData$data;
};

const SeriesBlock: React.FC<Props> = ({ series, ...props }) => {
    const { t } = useTranslation();

    if (!isSynced(series)) {
        return <VideoListBlockContainer title={series.title}>
            <div css={{ display: "flex", alignItems: "center", gap: 32, marginLeft: 16 }}>
                <MovingTruck css={{ flexShrink: 0 }} />
                <div css={{ maxWidth: 600 }}>{t("series.not-ready.text")}</div>
            </div>
        </VideoListBlockContainer>;
    }

    const seriesKey = keyOfId(series.id);
    return <VideoListBlock
        displayOptions={{
            initialLayout: props.layoutParam
                ?? (props.layout === "%future added value" ? "GALLERY" : props.layout),
            initialOrder: props.orderParam
                ?? (props.order === "%future added value" ? undefined : props.order)
                ?? "NEW_TO_OLD",
            allowOriginalOrder: false,
        }}
        listId={series.id}
        isBookmark={series.isBookmark}
        metadata={{
            title: props.title ?? (props.showTitle ? series.title : undefined),
            description: (props.showMetadata && series.description) || undefined,
            timestamp: props.showMetadata ? series.created ?? undefined : undefined,
            creators: props.showMetadata ? [...series.creators] : undefined,
            canWrite: series.canWrite,
        }}
        activeEventId={props.activeEventId}
        realmPath={props.realmPath}
        listEntries={series.entries}
        editMode={props.editMode ?? false}
        shareInfo={{
            kind: "series",
            shareUrl: props.realmPath == null
                ? DirectSeriesRoute.url({ seriesId: series.id })
                : SeriesRoute.url({ realmPath: props.realmPath, seriesId: series.id }),
            rssUrl: series.hasPublicVideos ? `/~rss/series/${seriesKey}` : undefined,
        }}
        linkToManagePage={ManageSeriesDetailsRoute.url({ id: series.id })}
    />;
};
